import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Camera, FileCode2, Landmark, FilePlus, Upload, Loader2, CheckCircle2, ReceiptText } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Auto-detect helper
function detectType(file) {
  const name = (file.name || "").toLowerCase();
  const type = (file.type || "").toLowerCase();
  if (type.startsWith("image/") || /\.(jpg|jpeg|png|webp|heic)$/.test(name)) return "image";
  if (name.endsWith(".xml") || type === "application/xml" || type === "text/xml") return "xml";
  if (name.endsWith(".csv") || type === "text/csv") return "csv";
  return null;
}

export default function UnifiedUpload({ trigger }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const [preview, setPreview] = useState(null); // { count, items:[{title,subtitle}], target }
  const [dragOver, setDragOver] = useState(false);
  const imgRef = useRef();
  const xmlRef = useRef();
  const csvRef = useRef();
  const paycheckRef = useRef();

  // Global drag&drop listener
  useEffect(() => {
    let counter = 0;
    const onEnter = (e) => { e.preventDefault(); counter++; if (e.dataTransfer?.types?.includes("Files")) setDragOver(true); };
    const onLeave = (e) => { e.preventDefault(); counter--; if (counter <= 0) { setDragOver(false); counter = 0; } };
    const onOver = (e) => e.preventDefault();
    const onDrop = async (e) => {
      e.preventDefault(); counter = 0; setDragOver(false);
      const files = Array.from(e.dataTransfer?.files || []);
      if (!files.length) return;
      const types = files.map(detectType);
      const first = types[0];
      if (!first) { toast.error("Formato non supportato. Usa JPG/PNG, XML o CSV."); return; }
      if (!types.every((t) => t === first)) { toast.error("Trascina file dello stesso tipo per volta."); return; }
      setOpen(true);
      if (first === "image") await processImages(files);
      else if (first === "xml") await processXml(files);
      else if (first === "csv") await processCsv(files[0]);
    };
    window.addEventListener("dragenter", onEnter);
    window.addEventListener("dragleave", onLeave);
    window.addEventListener("dragover", onOver);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragenter", onEnter);
      window.removeEventListener("dragleave", onLeave);
      window.removeEventListener("dragover", onOver);
      window.removeEventListener("drop", onDrop);
    };
  }, []);

  const processBustaPaga = async (files) => {
    setBusy("paycheck"); setPreview(null);
    const results = [];
    for (const f of files) {
      if (f.size > 8 * 1024 * 1024) { toast.error(`${f.name}: max 8MB`); continue; }
      try {
        const dataUrl = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f); });
        const b64 = dataUrl.split(",")[1];
        const isPdf = (f.type === "application/pdf") || /\.pdf$/i.test(f.name);
        const ocr = await api.post("/documents/busta-paga-ocr", {
          file_base64: b64,
          file_type: isPdf ? "application/pdf" : (f.type || "image/jpeg"),
          save_to_profile: true,  // aggiorna i valori reali nel profilo utente
        });
        const p = ocr.data.parsed;
        const audit = ocr.data.audit;
        const saved = ocr.data.saved_fields || [];
        const notes = [
          p.periodo && `Periodo ${p.periodo}`,
          p.retribuzione_lorda_annua_stimata && `RAL €${p.retribuzione_lorda_annua_stimata.toLocaleString("it-IT")}`,
          p.netto_mensile && `Netto €${p.netto_mensile}`,
        ].filter(Boolean).join(" · ");
        await api.post("/documents", {
          name: `Busta paga ${p.periodo || f.name} — ${p.azienda || "n.d."}`,
          category: "busta_paga",
          notes,
          file_data: dataUrl, file_type: f.type,
          parsed_data: { ...p, audit },
        });
        const nWarn = audit?.warnings?.length || 0;
        const subtitle = nWarn > 0
          ? `⚠️ ${nWarn} ${nWarn === 1 ? "anomalia rilevata" : "anomalie rilevate"} · Netto €${p.netto_mensile || "?"}`
          : `✓ Tutto coerente · RAL €${p.retribuzione_lorda_annua_stimata?.toLocaleString("it-IT") || "?"} · Netto €${p.netto_mensile || "?"}`;
        results.push({
          title: `${p.azienda || f.name}${p.periodo ? ` (${p.periodo})` : ""}`,
          subtitle,
          audit,
          saved,
        });
      } catch (err) {
        const msg = err?.response?.data?.detail || "Errore lettura busta paga";
        results.push({ title: f.name, subtitle: `❌ ${msg}` });
      }
    }
    setBusy(null); setPreview({ items: results, target: "/app/archivio", label: "Archivio", type: "busta_paga" });
  };

  const processImages = async (files) => {
    setBusy("image"); setPreview(null);
    const results = [];
    for (const f of files) {
      if (f.size > 4 * 1024 * 1024) { toast.error(`${f.name}: max 4MB`); continue; }
      try {
        const dataUrl = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f); });
        const b64 = dataUrl.split(",")[1];
        const ocr = await api.post("/documents/ocr", { image_base64: b64 });
        const p = ocr.data;
        const notes = [p.totale && `€ ${p.totale}`, p.iva != null && `IVA € ${p.iva}`, p.numero_documento && `N° ${p.numero_documento}`, p.descrizione].filter(Boolean).join(" · ");
        await api.post("/documents", {
          name: `${p.fornitore || f.name}${p.data ? " - " + p.data : ""}`,
          category: p.tipo || "ricevuta",
          notes, file_data: dataUrl, file_type: f.type, parsed_data: p,
        });
        results.push({ title: p.fornitore || f.name, subtitle: `${p.data || ""} · € ${p.totale || "?"} · ${p.categoria_suggerita || ""}` });
      } catch { results.push({ title: f.name, subtitle: "❌ Errore analisi" }); }
    }
    setBusy(null); setPreview({ items: results, target: "/app/archivio", label: "Archivio" });
  };

  const processXml = async (files) => {
    setBusy("xml"); setPreview(null);
    const results = [];
    for (const f of files) {
      try {
        const text = await f.text();
        const r = await api.post("/documents/parse-fatturapa", { xml_text: text });
        const p = r.data;
        const notes = [p.totale && `€ ${p.totale}`, p.iva != null && `IVA € ${p.iva}`, p.numero_documento && `N° ${p.numero_documento}`, p.descrizione].filter(Boolean).join(" · ");
        await api.post("/documents", {
          name: `${p.fornitore || f.name}${p.data ? " - " + p.data : ""}`,
          category: "fattura", notes, file_data: "", file_type: "application/xml", parsed_data: p,
        });
        results.push({ title: p.fornitore || f.name, subtitle: `${p.data || ""} · € ${p.totale || "?"} · N° ${p.numero_documento || "-"}` });
      } catch { results.push({ title: f.name, subtitle: "❌ XML non valido" }); }
    }
    setBusy(null); setPreview({ items: results, target: "/app/archivio", label: "Archivio" });
  };

  const processCsv = async (f) => {
    if (!f) return;
    setBusy("csv"); setPreview(null);
    try {
      const name = f.name.toLowerCase();
      let r;
      if (name.endsWith(".csv") || name.endsWith(".txt")) {
        const text = await f.text();
        r = await api.post("/transactions/import", { csv_text: text, bank_name: "" });
      } else {
        // Excel or PDF → multipart upload
        const fd = new FormData();
        fd.append("file", f);
        r = await api.post("/transactions/import-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      setPreview({ items: [{ title: `${r.data.imported} movimenti importati`, subtitle: `Categorizzati automaticamente dall'AI · da ${f.name}` }], target: "/app/movimenti", label: "Movimenti" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore analisi del file");
      setPreview({ items: [{ title: f.name, subtitle: "❌ Import fallito" }], target: "/app/movimenti", label: "Movimenti" });
    } finally { setBusy(null); }
  };

  const options = [
    { key: "image", icon: Camera, title: "Foto fattura o scontrino", desc: "JPG/PNG — OCR AI estrae fornitore, importo, IVA, data.", color: "bg-primary/10 text-primary", ref: imgRef },
    { key: "paycheck", icon: ReceiptText, title: "Busta paga", desc: "PDF/JPG — l'AI legge RAL, netto, detrazioni e verifica i calcoli.", color: "bg-secondary/10 text-secondary", ref: paycheckRef },
    { key: "xml", icon: FileCode2, title: "Fattura elettronica (XML)", desc: "File XML FatturaPA dalla tua PEC o gestionale.", color: "bg-secondary/10 text-secondary", ref: xmlRef },
    { key: "csv", icon: Landmark, title: "Estratto conto bancario", desc: "CSV, Excel (.xlsx/.xls) o PDF. L'AI categorizza ogni movimento.", color: "bg-accent text-accent-foreground", ref: csvRef },
    { key: "other", icon: FilePlus, title: "Altro documento", desc: "Certificati, contratti, PDF generici.", color: "bg-muted", onClick: () => { setOpen(false); nav("/app/archivio"); } },
  ];

  const closeAndReset = () => { setOpen(false); setPreview(null); setBusy(null); };

  return (
    <>
      <input ref={imgRef} type="file" accept="image/*" multiple onChange={(e) => processImages(Array.from(e.target.files || []))} className="hidden" />
      <input ref={xmlRef} type="file" accept=".xml" multiple onChange={(e) => processXml(Array.from(e.target.files || []))} className="hidden" />
      <input ref={csvRef} type="file" accept=".csv,.txt,.xlsx,.xls,.pdf" onChange={(e) => processCsv(e.target.files?.[0])} className="hidden" />
      <input ref={paycheckRef} type="file" accept="image/*,.pdf" multiple onChange={(e) => processBustaPaga(Array.from(e.target.files || []))} className="hidden" />

      {/* Full-page drop overlay */}
      {dragOver && (
        <div className="fixed inset-0 z-[100] bg-primary/95 flex items-center justify-center pointer-events-none" data-testid="drop-overlay">
          <div className="border-4 border-dashed border-primary-foreground/40 rounded-3xl p-12 text-center">
            <Upload className="w-20 h-20 mx-auto text-primary-foreground mb-4" />
            <div className="font-heading font-bold text-3xl text-primary-foreground mb-2">Rilascia qui il tuo file</div>
            <div className="text-primary-foreground/70 text-sm">L'AI riconosce automaticamente il tipo (foto, XML, CSV)</div>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={(o) => { if (!busy) setOpen(o); if (!o) setPreview(null); }}>
        {trigger && <div onClick={() => setOpen(true)}>{trigger}</div>}
        <DialogContent className="rounded-2xl max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {preview ? "Fatto ✓" : busy ? "Analisi AI in corso..." : "Cosa vuoi caricare?"}
            </DialogTitle>
            <DialogDescription>
              {preview ? "L'AI ha elaborato i tuoi file:" : busy ? "Non chiudere questa finestra." : "Scegli un tipo, o trascina un file ovunque nell'app: l'AI lo riconosce automaticamente."}
            </DialogDescription>
          </DialogHeader>

          {busy ? (
            <div className="py-12 flex flex-col items-center gap-3">
              <Loader2 className="w-10 h-10 animate-spin text-primary" />
            </div>
          ) : preview ? (
            <div className="space-y-3">
              <div className="space-y-2 max-h-[65vh] overflow-y-auto">
                {preview.items.map((it, i) => (
                  <div key={i} className="p-3 rounded-xl border bg-accent/30" data-testid={`preview-item-${i}`}>
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className={`w-5 h-5 flex-shrink-0 mt-0.5 ${it.audit?.warnings?.length ? "text-amber-600" : "text-primary"}`} />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm">{it.title}</div>
                        <div className="text-xs text-muted-foreground">{it.subtitle}</div>
                      </div>
                    </div>

                    {/* Audit dettagliato per busta paga */}
                    {it.audit && (it.audit.checks?.length > 0 || it.audit.warnings?.length > 0) && (
                      <div className="mt-3 space-y-2 pl-8" data-testid={`busta-audit-${i}`}>
                        {it.audit.warnings?.length > 0 && (
                          <div className="space-y-1.5" data-testid={`busta-warnings-${i}`}>
                            {it.audit.warnings.map((w, wi) => (
                              <div key={wi} className={`text-xs p-2 rounded-lg border ${w.severity === "high" ? "bg-red-50 border-red-200 text-red-800" : "bg-amber-50 border-amber-200 text-amber-800"}`} data-testid={`busta-warning-${i}-${wi}`}>
                                <div className="font-semibold uppercase tracking-wide text-[10px] mb-0.5">
                                  {w.severity === "high" ? "⚠ Anomalia importante" : "⚠ Verifica"}
                                </div>
                                {w.message}
                              </div>
                            ))}
                          </div>
                        )}
                        {it.audit.checks?.length > 0 && (
                          <details className="text-xs">
                            <summary className="cursor-pointer text-muted-foreground hover:text-foreground" data-testid={`busta-checks-toggle-${i}`}>
                              {it.audit.n_checks_ok}/{it.audit.n_checks_total} verifiche superate · Mostra dettagli
                            </summary>
                            <div className="mt-2 space-y-1">
                              {it.audit.checks.map((c, ci) => (
                                <div key={ci} className={`flex items-center justify-between gap-2 p-1.5 rounded ${c.ok ? "bg-primary/5" : "bg-amber-50"}`}>
                                  <span className={c.ok ? "text-foreground" : "text-amber-800"}>
                                    {c.ok ? "✓" : "⚠"} {c.label}
                                  </span>
                                  <span className="font-mono text-[11px] text-muted-foreground">
                                    atteso {c.expected} → letto {c.actual}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </details>
                        )}
                        {it.saved && it.saved.length > 0 && (
                          <div className="text-[11px] text-primary/80 italic" data-testid={`busta-saved-${i}`}>
                            ✓ Profilo aggiornato con dati reali: {it.saved.join(", ")}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={closeAndReset} className="rounded-full flex-1" data-testid="preview-close">Chiudi</Button>
                <Button onClick={() => { closeAndReset(); nav(preview.target); }} className="rounded-full flex-1" data-testid="preview-goto">Vai a {preview.label}</Button>
              </div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-3 mt-2">
              {options.map((o) => (
                <Card key={o.key} onClick={() => o.onClick ? o.onClick() : o.ref?.current?.click()}
                  className="p-5 rounded-2xl border cursor-pointer hover:-translate-y-1 hover:shadow-lg hover:border-primary/40 transition-all"
                  data-testid={`upload-option-${o.key}`}>
                  <div className={`w-11 h-11 rounded-xl ${o.color} flex items-center justify-center mb-3`}>
                    <o.icon className="w-5 h-5" />
                  </div>
                  <div className="font-heading font-semibold text-base mb-1">{o.title}</div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{o.desc}</p>
                </Card>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
